import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PurchaseProductPicker } from "@/components/PurchaseProductPicker";
import { AlertTriangle, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Ortak barkod çözümleme: tam eşleşme (SKU tahmini yok). El Terminali + Mağaza Satış kullanır.
export async function lookupBarcode(code) {
  const { data } = await api.get("/barcodes/lookup", { params: { code } });
  return data;
}

// "Barkod tanımlı değil" → yetkili kullanıcı ürün + beden seçip barkodu bağlar
export function UnknownBarcodeDialog({ code, onClose, onAssigned }) {
  const { can } = useAuth();
  const allowed = can("products", "edit");
  const [line, setLine] = useState({ material_id: "", product_name: "", variant_id: "", has_variants: false });
  const [mapOpen, setMapOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [owner, setOwner] = useState(null); // null=kontrol ediliyor, false=boş, {..}=zaten eşleşmiş
  useEffect(() => {
    setMapOpen(false); setOwner(null);
    if (!code) return;
    lookupBarcode(code).then((r) => setOwner(r.found ? { product_name: r.product.name, size: r.size || r.variant?.sku || "" } : (r.conflict || false)))
      .catch(() => setOwner(false));
  }, [code]);
  const ready = line.material_id && (!line.has_variants || line.variant_id);
  const assign = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/barcodes/assign", { code, product_id: line.material_id, variant_id: line.variant_id || "" });
      toast.success(`Barkod eşleştirildi: ${data.product.name}${data.size ? ` · ${data.size}` : ""}`);
      onAssigned?.(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!code} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm max-w-md" data-testid="unknown-barcode-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-300"><AlertTriangle className="w-5 h-5" /> {owner ? "Barkod zaten eşleşmiş" : "Barkod tanımlı değil"}</DialogTitle>
        </DialogHeader>
        <div className="font-mono text-lg text-slate-100 bg-slate-900 border border-slate-800 rounded-sm px-3 py-2" data-testid="unknown-barcode-code">{code}</div>
        {owner === null ? (
          <div className="text-sm text-slate-400 flex items-center gap-2" data-testid="unknown-barcode-checking"><Loader2 className="w-4 h-4 animate-spin" /> Barkod kontrol ediliyor…</div>
        ) : owner ? (
          <div className="text-sm text-rose-200 border border-rose-800 bg-rose-950/40 rounded-sm px-3 py-2" data-testid="barcode-already-mapped">
            Bu barkod zaten <b>{owner.product_name || "bir ürün"}</b>{owner.size ? <> / <b>{owner.size}</b></> : owner.level === "product" ? " (ürün kartında, bedensiz)" : ""} ile eşleşmiş. Yeni eşleştirme yapılamaz; gerekirse ürün kartından değiştirin.
          </div>
        ) : !allowed ? (
          <div className="text-sm text-slate-400" data-testid="unknown-barcode-no-perm">Barkod eşleştirme yetkiniz yok. Ürün Ara ile devam edebilirsiniz.</div>
        ) : !mapOpen ? (
          <Button onClick={() => setMapOpen(true)} className="w-full h-12 rounded-sm bg-blue-600 hover:bg-blue-500" data-testid="unknown-barcode-map-btn">
            <Link2 className="w-4 h-4 mr-2" /> Ürüne Eşleştir / Barkod Tanımla
          </Button>
        ) : (
          <div className="space-y-3">
            <PurchaseProductPicker index="map" line={line} onChange={(p) => setLine((l) => ({ ...l, ...p }))} />
            <Button onClick={assign} disabled={!ready || busy} className="w-full h-11 rounded-sm bg-emerald-600 hover:bg-emerald-500" data-testid="unknown-barcode-assign-btn">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Barkodu bu {line.has_variants ? "bedene" : "ürüne"} kaydet
            </Button>
          </div>
        )}
        <Button variant="outline" onClick={onClose} className="w-full rounded-sm border-slate-700 bg-transparent" data-testid="unknown-barcode-close">Kapat</Button>
      </DialogContent>
    </Dialog>
  );
}
